// src/context/AuthContext.js
// This file sets up a central place for your app to know the user's login status.

import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth'; // Firebase's function to listen for login changes
import { auth } from '../firebase/firebase'; // Make sure this path is correct for your Firebase auth instance
import signInRequest from '../handler/doRequest';
import {
  findUserEmail,
  getPublicGroups,
  getPublicActivities,
  updateGroup,
  createGroupII,
  denyReceivedRequest,
  acceptReceivedRequest,
  acceptJoinActivityReceivedRequest,
  addProperty,
  createActivity,
  denyJoinActivityReceivedRequest,
  updateUserProfile,
  getPublicUsers,
  updateGroupMembers,
  updateGroupMembersRemoveOne,
  deleteGroup,
  getUserByEmail,
  getGroupByName
} from '../api/valkiria25db.js';
import { useLocation, useNavigate } from 'react-router-dom';
// import { createActivity } from './MisBolosApi.js';


// 1. Create a "context" for our authentication status.
// Components will "read" from this context.
const AuthContext = createContext();

// 2. Create a custom React Hook to easily use this context.
export const useAuth = () => {
  return useContext(AuthContext);
}

// 3. Create the Provider component.
// This component will wrap your entire app (or relevant parts)
// and provide the login status to all its children.
export const AuthProvider = ({ children }) => {
  // `currentUser` will hold the Firebase User object if logged in, or null if logged out.
  const [currentUser, setCurrentUser] = useState(null);
  const [dbUser, setDbUser] = useState(null);
  // `isLoading` helps us know when Firebase has finished checking the initial login status.
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  // useEffect runs when the component mounts.
  // It sets up the Firebase listener.
  useEffect(() => {
    // `onAuthStateChanged` is Firebase's way to watch for login/logout events.
    // It returns an `unsubscribe` function to clean up the listener later.
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const idToken = await user?.getIdToken();
      console.log('IdToken?', idToken)
      console.log('onAuthStateChanged?', "<currentUser>")
      setCurrentUser(user); // Update our state with the current user (or null)
      setIsLoading(false); // We're done loading the auth state

      const checkingDbUser = async () => {
        const fetchedUser = await signInRequest(idToken);
        console.log('fetchedUser', fetchedUser)
        if (!fetchedUser) {
          console.error('❌ Failed to fetch user data from the server');
          return;
        }

        if (fetchedUser.isDummy) {
          console.log('🤕 The provided user is a dummy one... NOT!!!! Redirecting user to login screen')
          // navigate('/misbolos/login')
        }

        // Define a closure-aware helper to attach methods
        const attachHelpers = (userObj) => {
          console.log('Attaching helpers...')
          userObj.update = async () => {
            console.log('...updating user object from inside...')
            const refreshedUser = await signInRequest(idToken);
            console.log('...reattaching helpers...')
            attachHelpers(refreshedUser); // Re-attach helpers
            console.log('...replacing object...')
            setDbUser(refreshedUser);
            console.log('...should`ve worked...')
            return refreshedUser;
          };

          userObj.getPublicGroups = async (filter) => await getPublicGroups(idToken, filter);
          userObj.getGroupByName = async (groupName) => {
            if (!groupName) return console.error('getGroupByName needs a groupName!');
            return await getGroupByName(idToken, groupName);
          };
          userObj.getPublicActivities = async (filterName, maxDistance, userLocation) => {
            return await getPublicActivities(idToken, {
              activityName: filterName,
              maxDistanceKm: maxDistance,
              userLocationDict: userLocation,
            });
          };
          userObj.getPublicUsers = async (optQuery) => await getPublicUsers(idToken, optQuery);
          userObj.getUserByEmail = async (email) => {
            if (!email) return console.error('getUserByEmail needs an email!');
            return await getUserByEmail(idToken, email);
          };
          userObj.updateGroup = async (updateId, groupData) => {
            if (!updateId) return console.error('updateGroup needs an updateId!');
            return await updateGroup(idToken, { ...groupData, updateId });
          };
          userObj.updateGroupMembers = async (updateId, groupData) => {
            if (!updateId) return console.error('updateGroupMembers needs an updateId!');
            return await updateGroupMembers(idToken, { ...groupData, updateId });
          };
          userObj.updateGroupMembersRemoveOne = async (updateId, groupData) => {
            if (!updateId) return console.error('updateGroupMembersRemoveOne needs an updateId!');
            return await updateGroupMembersRemoveOne(idToken, { ...groupData, updateId });
          };
          userObj.deleteGroup = async (groupId) => {
            if (!groupId) return console.error('deleteGroup needs an groupId!');
            return await deleteGroup(idToken, groupId);
          };
          userObj.createGroup = async (groupData) => await createGroupII(idToken, groupData);
          userObj.denyReceivedRequest = async (requestId) => await denyReceivedRequest(idToken, requestId);
          userObj.acceptReceivedRequest = async (requestId) => await acceptReceivedRequest(idToken, requestId);
          userObj.acceptJoinActivityReceivedRequest = async (requestId) => await acceptJoinActivityReceivedRequest(idToken, requestId);
          userObj.denyJoinActivityReceivedRequest = async (requestId) => await denyJoinActivityReceivedRequest(idToken, requestId);
          userObj.findUserEmail = async (email) => await findUserEmail(idToken, email);
          userObj.addProperty = async (property) => await addProperty(idToken, property);
          userObj.updateUserProfile = async (data) => await updateUserProfile(idToken, data);
          userObj.createActivity = async (activity) => await createActivity(idToken, activity);
        };

        attachHelpers(fetchedUser);
        setDbUser(fetchedUser);
      };

      // if (user) {
      checkingDbUser()
      // }else{
      //   console.warn('Nothing to check because no session found 🔥')
      // }

    });

    // Cleanup function: stop listening when this component unmounts.
    return unsubscribe;
  }, []); // Empty dependency array means this effect runs once on mount.

  // The value we'll provide to any component that uses `useAuth()`.
  const value = {
    currentUser, // Who is logged in?
    isLoading,    // Are we still checking?
    dbUser
  };

  // Render children only when we're done loading the auth state.
  return (
    <AuthContext.Provider value={value}>
      {isLoading ? (
        // Show a loading message while Firebase checks the login status
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh', // Take full viewport height
          width: '100%',
          flexDirection: 'column'
        }}>
          <div className="spinner"></div> {/* The loading animation */}
          <p style={{ marginTop: '20px', fontSize: '1.2em', color: '#555' }}>Loading authentication...</p>
        </div>
      ) : (
        // Once loaded, show the rest of the app
        children
      )}
    </AuthContext.Provider>
  );
};